import type { CatalogItem, FormularyItem, RecipeLine, Route } from "@vaulted/shared";
import { DOSE_UNIT_LABELS, ROUTES, ROUTE_LABELS, componentDoses, doseToMl, formatDose, syringeUnits } from "@vaulted/shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link } from "react-router";
import { errorMessage, put } from "../api";
import { Alert, Button, Checkbox, Panel, PanelHeader } from "../components/ui";
import { useSession } from "../session";
import type { CatalogResponse } from "./Catalog";
import { RemoveButton } from "./settings/common";

const mlText = (ml: number | null) => (ml === null ? "—" : `${ml.toFixed(1)} mL`);

/** Small draws also show the U-100 syringe reading (1 unit = 0.01 mL), so it isn't mistaken for a dose. */
function DrawCell({ ml }: { ml: number | null }) {
  return (
    <>
      {mlText(ml)}
      {ml !== null && ml > 0 && ml < 1 && <div className="text-xs font-normal text-muted">{syringeUnits(ml)} units on a U-100 syringe</div>}
    </>
  );
}

interface DraftLine {
  formularyItemId: string;
  dose: string;
  route: Route | null;
  frequency: string;
}

const toDraft = (r: RecipeLine[]): DraftLine[] => r.map((l) => ({ formularyItemId: l.formularyItemId, dose: String(l.dose), route: l.route, frequency: l.frequency ?? "" }));

/** Edit what goes into a service or add-on. Needs "Manage formulary and recipes". */
export function RecipePanel({ item, data }: { item: CatalogItem; data: CatalogResponse }) {
  const qc = useQueryClient();
  const { can } = useSession();
  const manage = can("formulary.manage");
  const [lines, setLines] = useState<DraftLine[]>(toDraft(item.recipe));
  const [notice, setNotice] = useState<string | null>(null);
  useEffect(() => setLines(toDraft(item.recipe)), [item.recipe]);
  const byId = (id: string) => data.formulary.find((f) => f.id === id);
  const save = useMutation({
    mutationFn: () => put(`/api/catalog/items/${item.id}/recipe`, { lines: lines.map((l) => ({ formularyItemId: l.formularyItemId, dose: Number(l.dose), route: l.route, frequency: l.frequency.trim() || null })) }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["catalog"] });
      setNotice("Recipe saved.");
    },
  });
  const dirty = JSON.stringify(lines) !== JSON.stringify(toDraft(item.recipe));
  const available = data.formulary.filter((f) => f.active && !lines.some((l) => l.formularyItemId === f.id));
  const update = (i: number, patch: Partial<DraftLine>) => {
    setNotice(null);
    setLines((cur) => cur.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  };
  const included = data.items.filter((i) => item.includedAddOnIds.includes(i.id));

  return (
    <Panel>
      <PanelHeader
        title="Recipe"
        description={item.type === "service" ? "What goes into it. Doses use each medication's unit; mL comes from the strength in the formulary." : "What one of these adds to a drip."}
      />
      <form
        className="flex flex-col gap-4 px-5 py-5"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        {lines.length === 0 ? (
          <p className="text-sm text-muted">No recipe yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="table-base">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Dose</th>
                  <th>Draw</th>
                  <th>Route</th>
                  <th title="How often it can be given; printed on the menu">Freq</th>
                  {manage && <th />}
                </tr>
              </thead>
              <tbody>
                {lines.map((l, i) => {
                  const f = byId(l.formularyItemId);
                  const dose = Number(l.dose);
                  return (
                    <tr key={l.formularyItemId}>
                      <td>
                        <div className="font-semibold">{f?.name ?? "Unknown"}</div>
                        {f?.drugName && <div className="text-xs text-muted">{f.drugName}</div>}
                        {f && f.components.length > 0 && dose > 0 && (
                          <div className="text-xs text-ink-soft">
                            {componentDoses(f, dose)
                              .map((c) => `${c.name} ${formatDose(c.amount, c.unit)}`)
                              .join(", ")}
                          </div>
                        )}
                      </td>
                      <td className="whitespace-nowrap">
                        <input
                          aria-label={`${f?.name} dose`}
                          type="number"
                          step="any"
                          min={0}
                          required
                          disabled={!manage}
                          className="w-24 rounded-[var(--radius-control)] border border-line bg-white px-2 py-1.5 disabled:bg-mist"
                          value={l.dose}
                          onChange={(e) => update(i, { dose: e.target.value })}
                        />{" "}
                        <span className="text-sm text-muted">{f ? DOSE_UNIT_LABELS[f.doseUnit] : ""}</span>
                      </td>
                      <td className="text-sm whitespace-nowrap tabular-nums text-ink-soft">{f && dose > 0 ? mlText(doseToMl(f, dose)) : "—"}</td>
                      <td>
                        <select
                          aria-label={`${f?.name} route`}
                          disabled={!manage}
                          className="rounded-[var(--radius-control)] border border-line bg-white px-2 py-1.5 disabled:bg-mist"
                          value={l.route ?? ""}
                          onChange={(e) => update(i, { route: (e.target.value || null) as Route | null })}
                        >
                          <option value="">—</option>
                          {ROUTES.map((r) => (
                            <option key={r} value={r}>
                              {ROUTE_LABELS[r]}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <input
                          aria-label={`${f?.name} frequency`}
                          maxLength={20}
                          disabled={!manage}
                          placeholder="1/wk"
                          className="w-20 rounded-[var(--radius-control)] border border-line bg-white px-2 py-1.5 disabled:bg-mist"
                          value={l.frequency}
                          onChange={(e) => update(i, { frequency: e.target.value })}
                        />
                      </td>
                      {manage && (
                        <td>
                          <RemoveButton label={`Remove ${f?.name}`} onClick={() => (setNotice(null), setLines((cur) => cur.filter((_, j) => j !== i)))} />
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {manage && available.length > 0 && (
          <select
            aria-label="Add to recipe"
            className="self-start rounded-[var(--radius-control)] border border-line bg-white px-3 py-2 text-sm font-semibold text-brand"
            value=""
            onChange={(e) => {
              const f = byId(e.target.value);
              if (!f) return;
              setNotice(null);
              setLines((cur) => [...cur, { formularyItemId: f.id, dose: f.doseUnit === "each" ? "1" : "", route: f.kind === "medication" ? "iv" : null, frequency: "" }]);
            }}
          >
            <option value="">+ Add from formulary…</option>
            {available.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
        )}

        {item.type === "service" && (
          <p className="text-sm text-ink-soft">
            <span className="font-semibold">Already included:</span> {included.length ? included.map((a) => a.name).join(", ") : "none"}.{" "}
            <span className="text-muted">Worked out from the recipe; these boosts can't be added again or charged twice.</span>
          </p>
        )}

        {save.error ? <Alert>{errorMessage(save.error)}</Alert> : null}
        {notice && !dirty ? <Alert tone="ok">{notice}</Alert> : null}
        {manage ? (
          <div className="flex justify-end">
            <Button variant="primary" type="submit" busy={save.isPending} disabled={!dirty}>
              Save recipe
            </Button>
          </div>
        ) : (
          <p className="text-sm text-muted">Only people who manage the formulary can change recipes.</p>
        )}
        {data.formulary.length === 0 && (
          <Alert tone="info">
            Add medications and fluids in <Link to="/catalog/formulary" className="font-semibold underline">the formulary</Link> first.
          </Alert>
        )}
      </form>
    </Panel>
  );
}

interface SheetRow {
  source: string;
  f: FormularyItem;
  dose: number;
  route: Route | null;
}

/** The nurse's build sheet for a service, optionally with boosts. */
export function BuildSheet({
  item,
  data,
  extraIds,
  lotStrengths,
}: {
  item: CatalogItem;
  data: CatalogResponse;
  /** Fixed extras (charts): no boost picker. */
  extraIds?: string[];
  /** Strength of the lot picked on the chart, by formulary item (mL follows the vial actually used). */
  lotStrengths?: Record<string, number>;
}) {
  const [picked, setBoosts] = useState<string[]>([]);
  const boosts = extraIds ?? picked;
  const byId = (id: string) => {
    const f = data.formulary.find((x) => x.id === id);
    return f && lotStrengths?.[id] && f.strengthPerMl !== null ? { ...f, strengthPerMl: lotStrengths[id]! } : f;
  };
  const eligible = data.items.filter(
    (a) =>
      a.type === "add_on" &&
      a.status === "active" &&
      !item.includedAddOnIds.includes(a.id) &&
      ((item.categoryId && a.attachToCategoryIds.includes(item.categoryId)) || a.attachToServiceIds.includes(item.id)),
  );
  const rowsFor = (source: CatalogItem): SheetRow[] =>
    source.recipe.flatMap((l) => {
      const f = byId(l.formularyItemId);
      return f ? [{ source: source.name, f, dose: l.dose, route: l.route }] : [];
    });
  const rows = [...rowsFor(item), ...data.items.filter((a) => boosts.includes(a.id)).flatMap(rowsFor)];
  const meds = rows.filter((r) => r.f.kind === "medication");
  const other = rows.filter((r) => r.f.kind !== "medication");
  const totalMl = Math.round(meds.reduce((sum, r) => sum + (doseToMl(r.f, r.dose) ?? 0), 0) * 10) / 10;
  const missing = [item, ...data.items.filter((a) => boosts.includes(a.id))].filter((x) => x.recipe.length === 0).map((x) => x.name);

  return (
    <Panel>
      <PanelHeader title="Build sheet" description="What to draw for this service. A reference only; the chart records what was actually given." />
      <div className="flex flex-col gap-4 px-5 py-5">
        {!extraIds && eligible.length > 0 && (
          <fieldset>
            <legend className="mb-1 text-sm font-semibold text-ink-soft">With boosts</legend>
            <div className="grid gap-x-4 sm:grid-cols-2 lg:grid-cols-3">
              {eligible.map((a) => (
                <Checkbox key={a.id} label={a.name} checked={boosts.includes(a.id)} onChange={(on) => setBoosts((cur) => (on ? [...cur, a.id] : cur.filter((x) => x !== a.id)))} />
              ))}
            </div>
          </fieldset>
        )}

        {missing.length > 0 && <Alert tone="info">No recipe yet for: {missing.join(", ")}.</Alert>}

        {rows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="table-base">
              <thead>
                <tr>
                  <th>Draw</th>
                  <th>Medication</th>
                  <th>Dose</th>
                  <th>Route</th>
                  {boosts.length > 0 && <th className="hidden sm:table-cell">For</th>}
                </tr>
              </thead>
              <tbody>
                {meds.map((r, i) => (
                  <tr key={i}>
                    <td className="font-semibold whitespace-nowrap tabular-nums">
                      <DrawCell ml={doseToMl(r.f, r.dose)} />
                    </td>
                    <td>
                      {r.f.name}
                      {lotStrengths?.[r.f.id] && r.f.doseUnit !== "ml" && <span className="text-xs text-muted"> ({formatDose(r.f.strengthPerMl ?? 0, r.f.doseUnit)}/mL lot)</span>}
                      {r.f.components.length > 0 ? (
                        <ul className="mt-0.5 text-xs text-ink-soft">
                          {componentDoses(r.f, doseToMl(r.f, r.dose) ?? 0).map((c) => (
                            <li key={c.name}>
                              {c.name} {formatDose(c.amount, c.unit)}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        r.f.drugName && <div className="text-xs text-muted">{r.f.drugName}</div>
                      )}
                    </td>
                    <td className="whitespace-nowrap tabular-nums">{r.f.doseUnit === "ml" ? "—" : formatDose(r.dose, r.f.doseUnit)}</td>
                    <td>{r.route ? ROUTE_LABELS[r.route] : "—"}</td>
                    {boosts.length > 0 && <td className="hidden text-sm text-muted sm:table-cell">{r.source}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {(meds.length > 0 || other.length > 0) && (
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
            {meds.length > 0 && (
              <span>
                <span className="font-semibold">Total drawn:</span> {totalMl.toFixed(1)} mL
              </span>
            )}
            {other.map((r, i) => (
              <span key={i}>
                <span className="font-semibold">{r.f.name}:</span> {formatDose(r.dose, r.f.doseUnit)}
                {boosts.length > 0 && <span className="text-muted"> ({r.source})</span>}
              </span>
            ))}
          </div>
        )}
      </div>
    </Panel>
  );
}
